// src/api/brandApiKeys.ts
//
// Admin-only management of per-brand LLM API keys. Keys are sent to the backend
// over the existing TLS + JWT transport and are NEVER returned — responses only
// ever carry {configured, last4}.
import { apiFetch } from "./api-fetch";

export type LlmProvider = "openai" | "gemini" | "perplexity" | "claude";

export const LLM_PROVIDERS: LlmProvider[] = [
  "openai",
  "gemini",
  "perplexity",
  "claude",
];

export const PROVIDER_LABELS: Record<LlmProvider, string> = {
  openai: "OpenAI",
  gemini: "Gemini",
  perplexity: "Perplexity",
  claude: "Claude",
};

export interface ProviderKeyStatus {
  configured: boolean;
  last4: string;
  enabled: boolean;
}

export interface BrandLlmKeys {
  brand_name: string;
  mode: "global" | "custom";
  providers: Record<LlmProvider, ProviderKeyStatus>;
}

export interface UpdateBrandLlmKeysBody {
  mode?: "global" | "custom";
  /** Per-provider key. Omit or send "" to leave a provider unchanged. */
  keys?: Partial<Record<LlmProvider, string>>;
}

export interface TestKeyResult {
  provider: string;
  ok: boolean;
  detail: string;
  endpoint?: "sonar" | "agent";
}

const base = (brand: string) =>
  `/admin/brands/${encodeURIComponent(brand)}/llm-keys`;

export const brandApiKeysApi = {
  get: (brand: string) => apiFetch<BrandLlmKeys>(base(brand)),

  update: (brand: string, body: UpdateBrandLlmKeysBody) =>
    apiFetch<BrandLlmKeys>(base(brand), {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  remove: (brand: string, provider: LlmProvider) =>
    apiFetch<BrandLlmKeys>(`${base(brand)}/${provider}`, {
      method: "DELETE",
    }),

  /** Validate a key with a tiny live call. If `key` omitted, tests the stored
   * key. `endpoint` (#90, perplexity only): "sonar" (default) tests the
   * retiring Sonar chat-completions endpoint, "agent" tests the Agent API. */
  test: (brand: string, provider: LlmProvider, key?: string, endpoint?: "sonar" | "agent") =>
    apiFetch<TestKeyResult>(`${base(brand)}/${provider}/test`, {
      method: "POST",
      body: JSON.stringify({ key: key ?? null, endpoint: endpoint ?? null }),
    }),
};
